// src/Commoncontext/CompanyConfigContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

import { useSelectedCompany } from "./SelectedCompanyContext";
import * as CompanyConfigApi from "../Api/CompanyConfigApi";

const CompanyConfigContext = createContext(null);

export const CompanyConfigProvider = ({ children }) => {
  const { selectedCompanyId } = useSelectedCompany();

  // 🔹 Cache: { [companyId]: sidebarItems[] }
  const [sidebarCache, setSidebarCache] = useState({});
  // 🔹 Aktiivinen konfiguraatio
  const [sidebarItems, setSidebarItems] = useState([]);

  // 🔹 Frontpage cache: { [companyId]: frontpageConfigObject }
  const [frontpageCache, setFrontpageCache] = useState({});
  // 🔹 Aktiivinen frontpage config
  const [frontpageConfig, setFrontpageConfig] = useState(null);

  /* ============================================================================
     ✅ SETTINGS (UUSI) – cache + aktiivinen
     - ei koske sidebar/frontpage -toiminnallisuutta
  ============================================================================ */
  // 🔹 Settings cache: { [companyId]: settingsItems[] }
  const [settingsCache, setSettingsCache] = useState({});
  // 🔹 Aktiivinen settings config (effective items)
  const [settingsItems, setSettingsItems] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* ============================================================================
     Reagoi company-valintaan → Sidebar config
  ============================================================================ */
  useEffect(() => {
    if (!selectedCompanyId) {
      setSidebarItems([]);
      return;
    }

    // ✅ Cache-hit
    if (sidebarCache[selectedCompanyId]) {
      setSidebarItems(sidebarCache[selectedCompanyId]);
      return;
    }

    // 🔁 Cache-miss → hae backendistä
    let cancelled = false;
    setLoading(true);
    setError(null);

    CompanyConfigApi.getSidebarConfig(selectedCompanyId)
      .then((data) => {
        if (cancelled) return;

        const items = Array.isArray(data?.items) ? data.items : [];

        setSidebarCache((prev) => ({
          ...prev,
          [selectedCompanyId]: items,
        }));

        setSidebarItems(items);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("getSidebarConfig failed:", err);
          setError(err);
          setSidebarItems([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId, sidebarCache]);

  /* ============================================================================
     Reagoi company-valintaan → Frontpage config
     - haetaan vain kerran per companyId (cache)
  ============================================================================ */
  useEffect(() => {
    if (!selectedCompanyId) {
      setFrontpageConfig(null);
      return;
    }

    // ✅ Cache-hit
    if (frontpageCache[selectedCompanyId]) {
      setFrontpageConfig(frontpageCache[selectedCompanyId]);
      return;
    }

    // 🔁 Cache-miss → hae backendistä
    let cancelled = false;
    setLoading(true);
    setError(null);

    CompanyConfigApi.getFrontpageConfig(selectedCompanyId)
      .then((data) => {
        if (cancelled) return;

        setFrontpageCache((prev) => ({
          ...prev,
          [selectedCompanyId]: data || null,
        }));

        setFrontpageConfig(data || null);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("getFrontpageConfig failed:", err);
          setError(err);
          setFrontpageConfig(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId, frontpageCache]);

  /* ============================================================================
     ✅ SETTINGS (UUSI)
     Reagoi company-valintaan → Settings config
     - haetaan vain kerran per companyId (cache)
  ============================================================================ */
  useEffect(() => {
    if (!selectedCompanyId) {
      setSettingsItems([]);
      return;
    }

    // ✅ Cache-hit
    if (settingsCache[selectedCompanyId]) {
      setSettingsItems(settingsCache[selectedCompanyId]);
      return;
    }

    // 🔁 Cache-miss → hae backendistä
    let cancelled = false;
    setLoading(true);
    setError(null);

    CompanyConfigApi.getSettingsConfig(selectedCompanyId)
      .then((data) => {
        if (cancelled) return;

        const items = Array.isArray(data?.items) ? data.items : [];

        setSettingsCache((prev) => ({
          ...prev,
          [selectedCompanyId]: items,
        }));

        setSettingsItems(items);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("getSettingsConfig failed:", err);
          setError(err);
          setSettingsItems([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId, settingsCache]);

  /* ============================================================================
     Helper: hae frontpage config cachella (ja tarvittaessa backendistä)
     - tätä voi käyttää esim. Frontpage.js:ssä
  ============================================================================ */
  const getFrontpageConfigCached = useCallback(
    async (companyId) => {
      const id = String(companyId || "").trim();
      if (!id) return null;

      if (frontpageCache[id]) return frontpageCache[id];

      const data = await CompanyConfigApi.getFrontpageConfig(id);
      setFrontpageCache((prev) => ({ ...prev, [id]: data || null }));
      return data || null;
    },
    [frontpageCache]
  );

  /* ============================================================================
     Johdettu tieto
  ============================================================================ */
  const enabledItems = useMemo(() => {
    return sidebarItems
      .filter((i) => i.enabled !== false)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [sidebarItems]);

  /* ============================================================================
     ✅ SETTINGS (UUSI) – Johdettu tieto: näkyvät Settings-itemit
     - status: published | beta | hidden (backend normalisoi)
  ============================================================================ */
  const enabledSettingsItems = useMemo(() => {
    return settingsItems
      .sort((a, b) => (a.defaultOrder ?? 0) - (b.defaultOrder ?? 0));
  }, [settingsItems]);
  /* ============================================================================
   Update: Sidebar config (client-side effective update, no refetch)
============================================================================ */
  const updateSidebarConfig = useCallback(
    async (companyId, payloadItems, effectiveItems) => {
      const id = String(companyId || "").trim();
      if (!id) throw new Error("updateSidebarConfig: companyId puuttuu");
      // Backendille vain override-payload
      const cleanPayload = Array.isArray(payloadItems) ? payloadItems : [];
      // Tallenna backendille
      const res = await CompanyConfigApi.updateSidebarConfig(id, cleanPayload);
      // ✅ UI pidetään "effective"-datassa (sis. icon + route)
      const effective = Array.isArray(effectiveItems) ? effectiveItems : null;
      if (effective) {
        setSidebarCache((prev) => ({
          ...prev,
          [id]: effective,
        }));
        if (selectedCompanyId === id) {
          setSidebarItems(effective);
        }
      }
      return res;
    },
    [selectedCompanyId]
  );

  /* ============================================================================
     Context value
  ============================================================================ */
  const value = {
    // Sidebar
    sidebarItems,
    enabledItems,
    sidebarCache,
    setSidebarCache,
    updateSidebarConfig,

    // Frontpage
    frontpageConfig,
    frontpageCache,
    setFrontpageCache,
    getFrontpageConfigCached,

    // ✅ Settings (UUSI)
    settingsItems,
    enabledSettingsItems,
    settingsCache,
    setSettingsCache,

    // Status
    loading,
    error,
  };

  return (
    <CompanyConfigContext.Provider value={value}>
      {children}
    </CompanyConfigContext.Provider>
  );
};

export const useCompanyConfig = () => {
  const ctx = useContext(CompanyConfigContext);
  if (!ctx) {
    throw new Error("useCompanyConfig must be used inside CompanyConfigProvider");
  }
  return ctx;
};


